"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  FormState,
  UseCase,
  UseCaseId,
} from "@/(global)/contact/components/types";
import { Spinner } from "@/components/ui/Spinner";
import { StatusMessage } from "@/components/ui/StatusMessage";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { FORM_FIELD_CLASS } from "@/config/forms";
import {
  CONTACT_UI,
  type ContactUi,
} from "@/(global)/contact/components/contactDict";
import { ContactDetailsFields } from "@/(global)/contact/components/ContactDetailsFields";
import { withAttribution } from "@/lib/attribution-client";

const buildMessage = (
  state: FormState,
  labels: ContactUi["form"]["detailLabels"],
  blockTitle: string,
): string => {
  const extras: string[] = [];
  if (state.companyName) extras.push(`${labels.company}: ${state.companyName}`);
  if (state.eventName) extras.push(`${labels.event}: ${state.eventName}`);
  if (state.passengers)
    extras.push(`${labels.passengers}: ${state.passengers}`);
  if (state.pickup) extras.push(`${labels.pickup}: ${state.pickup}`);
  if (state.dropoff) extras.push(`${labels.dropoff}: ${state.dropoff}`);
  if (state.date) extras.push(`${labels.date}: ${state.date}`);
  if (state.time) extras.push(`${labels.time}: ${state.time}`);
  if (state.vehicle) extras.push(`${labels.vehicle}: ${state.vehicle}`);
  if (state.budget) extras.push(`${labels.budget}: ${state.budget}`);
  if (state.quoteRequested) extras.push(labels.quoteRequested);
  const extrasBlock =
    extras.length > 0 ? `\n\n${blockTitle}:\n- ${extras.join("\n- ")}` : "";
  return `${state.message}${extrasBlock}`.trim();
};

type ContactFormProps = {
  initialUseCases: UseCase[];
  selected: UseCaseId;
  locale?: Locale;
};

export const ContactForm = ({
  initialUseCases,
  selected,
  locale = "fr",
}: ContactFormProps): JSX.Element => {
  const t = tr(CONTACT_UI, locale).form;
  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: initialUseCases[0].subject,
    message: "",
    companyName: "",
    passengers: "",
    pickup: "",
    dropoff: "",
    date: "",
    time: "",
    vehicle: "",
    budget: "",
    eventName: "",
    honeypot: "",
    quoteRequested: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentUseCase = useMemo(
    () => initialUseCases.find((u) => u.id === selected)!,
    [initialUseCases, selected],
  );
  const showCompanyFields = selected === "entreprise" || selected === "groupes";

  useEffect(() => {
    if (currentUseCase) {
      setForm((prev) => ({ ...prev, subject: currentUseCase.subject }));
      setSuccess(null);
      setError(null);
    }
  }, [currentUseCase]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onToggleQuote = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, quoteRequested: e.target.checked }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    setError(null);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: buildMessage(form, t.detailLabels, t.detailsBlockTitle),
        honeypot: form.honeypot ?? "",
        formType: selected,
        quoteRequested: !!form.quoteRequested,
        companyName: form.companyName || undefined,
        passengers: form.passengers || undefined,
        pickup: form.pickup || undefined,
        dropoff: form.dropoff || undefined,
        date: form.date || undefined,
        time: form.time || undefined,
        vehicle: form.vehicle || undefined,
        budget: form.budget || undefined,
        eventName: form.eventName || undefined,
        locale,
        sourcePage:
          typeof window !== "undefined" ? window.location.href : undefined,
      };

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withAttribution(payload)),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || t.genericError);
      setSuccess(t.success);
      setForm((prev) => ({
        ...prev,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: currentUseCase.subject,
        message: "",
        companyName: "",
        passengers: "",
        pickup: "",
        dropoff: "",
        date: "",
        time: "",
        vehicle: "",
        budget: "",
        eventName: "",
        honeypot: "",
        quoteRequested: false,
      }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t.unexpectedError;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lg:col-span-8">
      <form
        onSubmit={onSubmit}
        className="space-y-5 rounded-2xl border border-black/10 bg-white p-5 text-black sm:p-6"
      >
        <div className="space-y-1">
          <p className="text-sm font-semibold text-black">
            {currentUseCase.label}
          </p>
          <p className="text-sm text-black/70">{t.intro}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={FORM_FIELD_CLASS.label}>{t.firstName}</span>
            <input
              id="firstName"
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              required
              minLength={2}
              className={FORM_FIELD_CLASS.input}
            />
          </label>
          <label className="block">
            <span className={FORM_FIELD_CLASS.label}>{t.lastName}</span>
            <input
              id="lastName"
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              required
              minLength={2}
              className={FORM_FIELD_CLASS.input}
            />
          </label>
          <label className="block">
            <span className={FORM_FIELD_CLASS.label}>{t.email}</span>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              required
              className={FORM_FIELD_CLASS.input}
            />
          </label>
          <label className="block">
            <span className={FORM_FIELD_CLASS.label}>{t.phone}</span>
            <input
              id="phone"
              name="phone"
              type="tel"
              value={form.phone}
              onChange={onChange}
              placeholder={t.phonePlaceholder}
              pattern={t.phonePattern}
              title={t.phoneTitle}
              className={FORM_FIELD_CLASS.input}
            />
          </label>
        </div>

        <div className="space-y-3">
          <div>
            <label className={FORM_FIELD_CLASS.label} htmlFor="subject">
              {t.subject}
            </label>
            <input
              id="subject"
              name="subject"
              value={form.subject}
              onChange={onChange}
              required
              className={FORM_FIELD_CLASS.input}
            />
          </div>
          <div>
            <label className={FORM_FIELD_CLASS.label} htmlFor="message">
              {t.message}
            </label>
            <textarea
              id="message"
              name="message"
              value={form.message}
              onChange={onChange}
              required
              minLength={5}
              rows={5}
              className="w-full resize-y rounded-lg border border-black/15 bg-white px-3 py-2.5 text-sm text-black outline-none ring-primary/40 transition focus:ring-2"
              placeholder={t.messagePlaceholder}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-black/85">
            <input
              type="checkbox"
              checked={!!form.quoteRequested}
              onChange={onToggleQuote}
              className="h-4 w-4 rounded border-black/20 bg-white text-primary focus:ring-primary/40"
            />
            {t.quote}
          </label>
        </div>

        <ContactDetailsFields
          form={form}
          onChange={onChange}
          showCompanyFields={showCompanyFields}
          t={t}
        />

        <div className="hidden" aria-hidden="true">
          <label htmlFor="honeypot">{t.honeypotLabel}</label>
          <input
            id="honeypot"
            name="honeypot"
            value={form.honeypot}
            onChange={onChange}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {success && <StatusMessage type="success" message={success} />}
        {error && <StatusMessage type="error" message={error} />}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Spinner />
                {t.sending}
              </>
            ) : (
              <>{t.submit}</>
            )}
          </button>
          <Link
            href={locale === "fr" ? "/" : `/${locale}`}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-black/15 bg-black/[0.02] px-5 text-sm font-semibold text-black/90 transition-colors hover:border-primary"
          >
            {t.backHome}
          </Link>
        </div>
      </form>
    </div>
  );
};
